// @file Composable to handle attachment uploading for posts/drafts.
// NOTE: This composable is an extension of `surface_drafts` Pinia store
// so we MUST NOT destructure Pinia stores.
import { __ } from '@@/bits/intl'
import { getAttachmentsFromDraft } from '@@/bits/post_drafts'
import { FILE_TOO_LARGE_ERROR, startUpload, stopUpload } from '@@/bits/uploader'
import { unboundedWatch } from '@@/bits/vue'
import { useComposerModalAlertStore } from '@@/pinia/composer_modal_alert'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Cid, UploadedFile } from '@@/types'
import useLogger from '@@/vuecomposables/logger'
import { computed } from 'vue'

const logger = useLogger('post-attachment-uploader', { logLifecycleHooks: false, log: true })

export const usePostAttachmentUploader = (): {
  startUploadFile: typeof startUploadFile
  stopUploadFile: typeof stopUploadFile
} => {
  const surfaceStore = useSurfaceStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const composerModalAlertStore = useComposerModalAlertStore()
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const surfacePostsStore = useSurfacePostsStore()

  const startUploadFile = ({
    file,
    maxFileUploadSize,
    cid,
    trimVideoAfterUpload,
    attachmentIndex,
  }: {
    file: UploadedFile
    maxFileUploadSize?: number
    cid: Cid
    trimVideoAfterUpload?: { start: number; end: number }
    attachmentIndex?: number
  }): void => {
    // Google Drive files are uploaded by Beethoven, we don't do it here.
    if (file.isGoogleDriveFile === true) return

    const currentDraft = surfaceDraftsStore.draftByCid[cid]

    const uploadJob = startUpload(file, { maxFileSize: maxFileUploadSize, trimVideoAfterUpload })

    if (trimVideoAfterUpload != null) {
      showVideoTrimmingSnackbar()
    }

    uploadJob.on('progress', (progress) => {
      logger.log(`Upload job progress: ${progress as unknown as number}`)
      if (attachmentIndex == null) {
        surfaceDraftsStore.updateDraft({ cid, uploadProgress: progress })
        return
      }
      const draft = surfaceDraftsStore.draftByCid[cid]
      if (draft == null || draft.attachments == null) return

      const updatedAttachments = [...draft.attachments]
      if (updatedAttachments[attachmentIndex] != null) {
        updatedAttachments[attachmentIndex] = { ...updatedAttachments[attachmentIndex], progress }
        surfaceDraftsStore.updateDraft({ cid, attachments: updatedAttachments })
      }
    })

    const attachmentKey = attachmentIndex != null ? attachmentIndex.toString() : undefined
    uploadJob.on('done', ({ url }) => {
      logger.log(`Upload job done: ${url as unknown as string}`)
      deleteUploadedFile({ cid, attachmentKey })
      if (attachmentIndex == null) {
        surfaceDraftsStore.updateDraft({
          cid,
          attachment: url,
          wish_content: {
            attachment_props: { url },
            is_processed: false,
          },
          uploadingFile: null,
          uploadProgress: 100,
        })
      } else {
        const draft = surfaceDraftsStore.draftByCid[cid]
        if (draft == null) return

        const attachments = getAttachmentsFromDraft(draft)
        if (attachments[attachmentIndex] != null) {
          attachments[attachmentIndex] = {
            ...attachments[attachmentIndex],
            url,
            isUploadingFile: false,
            fileBeingUploaded: undefined,
            progress: 100,
          }
          surfaceDraftsStore.updateDraft({ cid, attachments })
        }
      }

      const updatedCurrentDraft = surfaceDraftsStore.draftByCid[cid]
      if (updatedCurrentDraft?.shouldAutoPublish === true) {
        surfaceDraftsStore.stopAutoPublishDraft(cid) // when upload is done, stop auto publish
        void surfacePostsStore.savePost({ attributes: updatedCurrentDraft })
      }
    })

    uploadJob.on('error', (err) => {
      logger.log(`Upload job done: ${err as unknown as string}`)
      if (attachmentIndex == null) {
        surfaceDraftsStore.updateDraft({ cid, uploadError: `Upload job failed: ${Number(err.status)}` })
        surfaceDraftsStore.stopUploadFileInDraft(cid)
        surfaceDraftsStore.stopAutoPublishDraft(cid)
        return
      } else {
        deleteUploadedFile({ cid, attachmentKey })

        if (attachmentIndex == null) {
          surfaceDraftsStore.updateDraft({ cid, uploadError: `Upload job failed: ${Number(err.status)}` })
          surfaceDraftsStore.stopUploadFileInDraft(cid)
        } else {
          const draft = surfaceDraftsStore.draftByCid[cid]
          if (draft == null) return

          if (draft.attachments != null) {
            const updatedAttachments = [...draft.attachments]
            if (updatedAttachments[attachmentIndex] != null) {
              updatedAttachments[attachmentIndex] = {
                ...updatedAttachments[attachmentIndex],
                isUploadingFile: false,
                fileBeingUploaded: undefined,
                progress: 0,
                error: `Upload job failed: ${Number(err.status)}`,
              } as any
              surfaceDraftsStore.updateDraft({ cid, attachments: updatedAttachments })
            }
          }
        }
      }

      if (err.error === FILE_TOO_LARGE_ERROR) {
        surfaceStore.showOverFileSizeModal({ file })
        return
      }

      const message = __('Sorry, there was an error uploading your file.')

      if (surfaceDraftsStore.isCidActiveDraft(cid)) {
        globalSnackbarStore.setSnackbar({ message })
        return
      }

      composerModalAlertStore.openComposerAlert({
        title: __('There was an error loading the attachment'),
        body: message,
        primaryActionText: __('Try again'),
        secondaryActionText: __('Cancel'),
        primaryActions: [
          () =>
            surfaceDraftsStore.startUploadFileInDraft({
              cid,
              file,
              maxFileUploadSize: currentDraft?.maxFileUploadSize,
            }),
        ],
        secondaryActions: [],
      })
    })

    surfaceDraftsStore.setUploadJob({ cid, uploadJob, attachmentKey })
  }

  const deleteUploadedFile = ({ cid, attachmentKey }: { cid: Cid; attachmentKey?: string }): void => {
    const uploader = surfaceDraftsStore.getUploadJob(cid, attachmentKey)
    if (uploader == null) return
    stopUpload(uploader.file)
    surfaceDraftsStore.deleteUploadJob({ cid, attachmentKey })
  }

  const stopUploadFile = ({ cid, attachmentIndex }: { cid: Cid; attachmentIndex?: number }): void => {
    const attachmentKey = attachmentIndex != null ? attachmentIndex.toString() : undefined
    deleteUploadedFile({ cid, attachmentKey })
    surfaceDraftsStore.stopAutoPublishDraft(cid)

    const draft = surfaceDraftsStore.draftByCid[cid]
    if (draft == null) return

    if (attachmentIndex == null) {
      surfaceDraftsStore.updateDraft({ cid, uploadingFile: null, uploadProgress: 0 })
    } else {
      if (draft.attachments != null) {
        const updatedAttachments = [...draft.attachments]
        if (updatedAttachments[attachmentIndex] != null) {
          updatedAttachments[attachmentIndex] = {
            ...updatedAttachments[attachmentIndex],
            isUploadingFile: false,
            fileBeingUploaded: undefined,
            progress: 0,
          }
          surfaceDraftsStore.updateDraft({ cid, attachments: updatedAttachments })
        }
      }
    }
  }

  const pendingPostReadyToUploadCids = computed<Cid[]>(() => {
    const pendingPostsWithFile = surfacePostsStore.pendingPosts.filter((post) => Boolean(post.file))

    // If there are posts for which sections are yet to be picked,
    // don't start uploading them, filter them out. Only do it for
    // sectioned walls because that's the only occasion where we
    // ask users to pick a section.
    if (surfaceStore.canUseSections && surfaceStore.isSectioned) {
      const pendingPostsWithSections = pendingPostsWithFile.filter((x) => Boolean(x.wall_section_id))
      return pendingPostsWithSections.map((post) => post.cid)
    }

    return pendingPostsWithFile.map((post) => post.cid)
  })

  void unboundedWatch(pendingPostReadyToUploadCids, (cids) => {
    if (cids.length === 0) return
    cids.forEach((cid) => {
      const postAttributes = surfacePostsStore.pendingPostEntitiesByCid[cid]
      if (postAttributes.file == null) return
      surfaceDraftsStore.createNewDraftFromPendingUpload({
        file: postAttributes.file,
        postAttributes,
      })
    })
    surfacePostsStore.removePendingPosts({ cids })
  })

  const showVideoTrimmingSnackbar = (): void => {
    globalSnackbarStore.setSnackbar({
      message: __('Your video is processing. This may take a few minutes.'),
    })
  }

  return { startUploadFile, stopUploadFile }
}
